import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cicloMelhoriaApi, ideiasApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Target,
  Plus,
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  Loader2,
  Edit,
  Trash2,
  Lightbulb,
  ArrowRight,
  PlayCircle,
  CheckCircle,
  XCircle,
  FileText,
  BarChart3
} from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-slate-500', icon: Clock },
  em_andamento: { label: 'Em Andamento', color: 'bg-amber-500', icon: PlayCircle },
  concluido: { label: 'Concluído', color: 'bg-emerald-500', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-red-500', icon: XCircle }
};

const CICLO_STATUS = {
  planejamento: { label: 'Planejamento', color: 'bg-blue-500' },
  em_andamento: { label: 'Em Andamento', color: 'bg-amber-500' },
  concluido: { label: 'Concluído', color: 'bg-emerald-500' }
};

const CicloMelhoriaPage = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ciclos, setCiclos] = useState([]);
  const [cicloAtual, setCicloAtual] = useState(null);
  const [ideiasPendentes, setIdeiasPendentes] = useState([]);
  const [activeTab, setActiveTab] = useState('atual');
  
  // Dialogs
  const [showNovoCiclo, setShowNovoCiclo] = useState(false);
  const [showNovoProblema, setShowNovoProblema] = useState(false);
  const [showImportarIdeia, setShowImportarIdeia] = useState(false);
  const [showEditProblema, setShowEditProblema] = useState(false);
  
  // Forms
  const [novoCicloForm, setNovoCicloForm] = useState({
    titulo: '',
    data_inicio: format(new Date(), 'yyyy-MM-dd'),
    data_fim: '',
    descricao: ''
  });
  
  const [novoProblemaForm, setNovoProblemaForm] = useState({
    descricao: '',
    causa_raiz: '',
    solucao_proposta: '',
    responsavel: '',
    data_prevista: '',
    prioridade: 'media'
  });
  
  const [problemaEdit, setProblemaEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ciclosRes, ideiasRes] = await Promise.all([
        cicloMelhoriaApi.list(),
        ideiasApi.listar({ tipo: 'reclamacao', status: 'pendente' })
      ]);
      
      setCiclos(ciclosRes.data || []);
      setIdeiasPendentes(ideiasRes.data || []);
      
      // Find current cycle
      const atual = ciclosRes.data?.find(c => c.status === 'em_andamento') || 
                    ciclosRes.data?.find(c => c.status === 'planejamento');
      setCicloAtual(atual);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar ciclos de melhoria');
    } finally {
      setLoading(false);
    }
  };

  const handleCriarCiclo = async () => {
    if (!novoCicloForm.titulo || !novoCicloForm.data_inicio) {
      toast.error('Preencha o título e data de início');
      return;
    }
    
    setSaving(true);
    try {
      await cicloMelhoriaApi.create(novoCicloForm);
      toast.success('Ciclo de melhoria criado!');
      setShowNovoCiclo(false);
      setNovoCicloForm({
        titulo: '',
        data_inicio: format(new Date(), 'yyyy-MM-dd'),
        data_fim: '',
        descricao: ''
      });
      loadData();
    } catch (error) {
      toast.error('Erro ao criar ciclo');
    } finally {
      setSaving(false);
    }
  };

  const handleAdicionarProblema = async () => {
    if (!novoProblemaForm.descricao) {
      toast.error('Descreva o problema');
      return;
    }
    
    if (!cicloAtual) {
      toast.error('Selecione ou crie um ciclo primeiro');
      return;
    }
    
    setSaving(true);
    try {
      await cicloMelhoriaApi.addProblema(cicloAtual.id, {
        ...novoProblemaForm,
        origem: 'manual'
      });
      toast.success('Problema adicionado ao ciclo!');
      setShowNovoProblema(false);
      setNovoProblemaForm({
        descricao: '',
        causa_raiz: '',
        solucao_proposta: '',
        responsavel: '',
        data_prevista: '',
        prioridade: 'media'
      });
      loadData();
    } catch (error) {
      toast.error('Erro ao adicionar problema');
    } finally {
      setSaving(false);
    }
  };

  const handleImportarIdeia = async (ideia) => {
    if (!cicloAtual) {
      toast.error('Selecione ou crie um ciclo primeiro');
      return;
    }
    
    setSaving(true);
    try {
      await cicloMelhoriaApi.addProblema(cicloAtual.id, {
        descricao: ideia.descricao,
        causa_raiz: '',
        solucao_proposta: '',
        responsavel: '',
        data_prevista: '',
        prioridade: 'media',
        origem: 'banco_ideias',
        ideia_id: ideia.id
      });
      toast.success('Ideia importada para o ciclo!');
      loadData();
    } catch (error) {
      toast.error('Erro ao importar ideia');
    } finally {
      setSaving(false);
    }
  };

  const handleAtualizarStatusProblema = async (problemaId, novoStatus) => {
    if (!cicloAtual) return;
    
    try {
      await cicloMelhoriaApi.updateProblema(cicloAtual.id, problemaId, { 
        status: novoStatus,
        data_conclusao: novoStatus === 'concluido' ? format(new Date(), 'yyyy-MM-dd') : null
      });
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleAtualizarStatusCiclo = async (novoStatus) => {
    if (!cicloAtual) return;
    
    try {
      await cicloMelhoriaApi.update(cicloAtual.id, { status: novoStatus });
      toast.success('Status do ciclo atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar ciclo');
    }
  };

  const handleEditarProblema = async () => {
    if (!problemaEdit || !cicloAtual) return;
    
    setSaving(true);
    try {
      await cicloMelhoriaApi.updateProblema(cicloAtual.id, problemaEdit.id, problemaEdit);
      toast.success('Problema atualizado!');
      setShowEditProblema(false);
      setProblemaEdit(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar problema');
    } finally {
      setSaving(false);
    }
  };

  const calcularProgresso = (ciclo) => {
    if (!ciclo?.problemas?.length) return 0;
    const concluidos = ciclo.problemas.filter(p => p.status === 'concluido').length;
    return Math.round((concluidos / ciclo.problemas.length) * 100);
  };

  const getResumo = (ciclo) => {
    if (!ciclo?.problemas?.length) return { total: 0, pendentes: 0, andamento: 0, concluidos: 0 };
    return {
      total: ciclo.problemas.length,
      pendentes: ciclo.problemas.filter(p => p.status === 'pendente').length,
      andamento: ciclo.problemas.filter(p => p.status === 'em_andamento').length,
      concluidos: ciclo.problemas.filter(p => p.status === 'concluido').length
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const resumo = cicloAtual ? getResumo(cicloAtual) : null;
  const progresso = cicloAtual ? calcularProgresso(cicloAtual) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Target className="w-7 h-7 text-cyan-400" />
            Ciclo de Melhoria Contínua
          </h1>
          <p className="text-zinc-400 mt-1">
            Acompanhamento de problemas identificados e ações corretivas
          </p>
        </div>
        {isAdmin() && (
          <Button 
            onClick={() => setShowNovoCiclo(true)}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Ciclo
          </Button>
        )}
      </div>

      {/* Ciclo Atual Overview */}
      {cicloAtual ? (
        <Card className="bg-gradient-to-r from-[#141416] to-[#1a1a1e] border-cyan-500/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  {cicloAtual.titulo}
                  <Badge className={`${CICLO_STATUS[cicloAtual.status]?.color} text-white ml-2`}>
                    {CICLO_STATUS[cicloAtual.status]?.label}
                  </Badge>
                </CardTitle>
                <CardDescription className="text-zinc-400 mt-1">
                  {cicloAtual.data_inicio && format(parseISO(cicloAtual.data_inicio), "dd/MM/yyyy", { locale: ptBR })}
                  {cicloAtual.data_fim && ` até ${format(parseISO(cicloAtual.data_fim), "dd/MM/yyyy", { locale: ptBR })}`}
                </CardDescription>
              </div>
              {isAdmin() && cicloAtual.status !== 'concluido' && (
                <div className="flex gap-2">
                  {cicloAtual.status === 'planejamento' && (
                    <Button 
                      size="sm" 
                      onClick={() => handleAtualizarStatusCiclo('em_andamento')}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <PlayCircle className="w-4 h-4 mr-1" />
                      Iniciar Ciclo
                    </Button>
                  )}
                  {cicloAtual.status === 'em_andamento' && progresso === 100 && (
                    <Button 
                      size="sm" 
                      onClick={() => handleAtualizarStatusCiclo('concluido')}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Concluir Ciclo
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {cicloAtual.descricao && (
              <p className="text-zinc-300 text-sm mb-4 bg-zinc-900/50 p-3 rounded-lg">
                {cicloAtual.descricao}
              </p>
            )}
            
            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-zinc-400">Progresso do Ciclo</span>
                <span className="text-cyan-400 font-mono font-bold">{progresso}%</span>
              </div>
              <Progress value={progresso} className="h-3 bg-zinc-800" />
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{resumo?.total || 0}</p>
                <p className="text-xs text-zinc-500 uppercase">Total</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-400">{resumo?.pendentes || 0}</p>
                <p className="text-xs text-zinc-500 uppercase">Pendentes</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-400">{resumo?.andamento || 0}</p>
                <p className="text-xs text-zinc-500 uppercase">Em Andamento</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">{resumo?.concluidos || 0}</p>
                <p className="text-xs text-zinc-500 uppercase">Concluídos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#141416] border-zinc-800">
          <CardContent className="py-12 text-center">
            <Target className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
            <h3 className="text-lg font-medium text-zinc-400 mb-2">Nenhum ciclo ativo</h3>
            <p className="text-zinc-500 text-sm mb-4">Crie um novo ciclo de melhoria para começar a acompanhar os problemas</p>
            {isAdmin() && (
              <Button onClick={() => setShowNovoCiclo(true)} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Ciclo
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="atual" className="data-[state=active]:bg-cyan-600">
            <FileText className="w-4 h-4 mr-2" />
            Problemas do Ciclo
          </TabsTrigger>
          <TabsTrigger value="ideias" className="data-[state=active]:bg-cyan-600">
            <Lightbulb className="w-4 h-4 mr-2" />
            Banco de Ideias
            {ideiasPendentes.length > 0 && (
              <Badge className="ml-2 bg-amber-500 text-xs">{ideiasPendentes.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="historico" className="data-[state=active]:bg-cyan-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Histórico
          </TabsTrigger>
        </TabsList>

        {/* Tab: Problemas do Ciclo */}
        <TabsContent value="atual" className="mt-4">
          <Card className="bg-[#141416] border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Problemas Identificados</CardTitle>
              {isAdmin() && cicloAtual && (
                <Button onClick={() => setShowNovoProblema(true)} size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Problema
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {cicloAtual?.problemas?.length > 0 ? (
                <div className="space-y-4">
                  {cicloAtual.problemas.map((problema) => {
                    const StatusIcon = STATUS_CONFIG[problema.status]?.icon || Clock;
                    const diasRestantes = problema.data_prevista ? 
                      differenceInDays(parseISO(problema.data_prevista), new Date()) : null;
                    
                    return (
                      <div 
                        key={problema.id}
                        className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800 hover:border-zinc-700 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`${STATUS_CONFIG[problema.status]?.color} text-white text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {STATUS_CONFIG[problema.status]?.label}
                              </Badge>
                              {problema.origem === 'banco_ideias' && (
                                <Badge variant="outline" className="text-amber-400 border-amber-400/50 text-xs">
                                  <Lightbulb className="w-3 h-3 mr-1" />
                                  Banco de Ideias
                                </Badge>
                              )}
                              {problema.prioridade === 'alta' && (
                                <Badge className="bg-red-500/20 text-red-400 text-xs">Prioridade Alta</Badge>
                              )}
                            </div>
                            
                            <h4 className="text-white font-medium mb-2">{problema.descricao}</h4>
                            
                            {problema.causa_raiz && (
                              <div className="mb-2">
                                <span className="text-zinc-500 text-xs uppercase">Causa Raiz:</span>
                                <p className="text-zinc-300 text-sm">{problema.causa_raiz}</p>
                              </div>
                            )}
                            
                            {problema.solucao_proposta && (
                              <div className="mb-2">
                                <span className="text-zinc-500 text-xs uppercase">Solução Proposta:</span>
                                <p className="text-zinc-300 text-sm">{problema.solucao_proposta}</p>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                              {problema.responsavel && (
                                <span>Responsável: <span className="text-zinc-300">{problema.responsavel}</span></span>
                              )}
                              {problema.data_prevista && (
                                <span className={diasRestantes !== null && diasRestantes < 0 ? 'text-red-400' : ''}>
                                  Previsto: <span className="text-zinc-300">{format(parseISO(problema.data_prevista), "dd/MM/yyyy")}</span>
                                  {diasRestantes !== null && diasRestantes < 0 && ' (Atrasado)'}
                                </span>
                              )}
                              {problema.data_conclusao && (
                                <span className="text-emerald-400">
                                  Concluído: {format(parseISO(problema.data_conclusao), "dd/MM/yyyy")}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {isAdmin() && (
                            <div className="flex flex-col gap-2">
                              {problema.status !== 'concluido' && (
                                <>
                                  {problema.status === 'pendente' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => handleAtualizarStatusProblema(problema.id, 'em_andamento')}
                                      className="text-amber-400 border-amber-400/50 hover:bg-amber-400/10"
                                    >
                                      <PlayCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleAtualizarStatusProblema(problema.id, 'concluido')}
                                    className="text-emerald-400 border-emerald-400/50 hover:bg-emerald-400/10"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setProblemaEdit(problema);
                                  setShowEditProblema(true);
                                }}
                                className="text-zinc-400 hover:text-white"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum problema cadastrado neste ciclo</p>
                  {isAdmin() && cicloAtual && (
                    <Button 
                      onClick={() => setShowNovoProblema(true)} 
                      variant="outline" 
                      className="mt-4"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Primeiro Problema
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Banco de Ideias */}
        <TabsContent value="ideias" className="mt-4">
          <Card className="bg-[#141416] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-400" />
                Reclamações/Sugestões do Banco de Ideias
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Importe problemas identificados pelos colaboradores para o ciclo de melhoria
              </CardDescription>
            </CardHeader>
            <CardContent>
              {ideiasPendentes.length > 0 ? (
                <div className="space-y-3">
                  {ideiasPendentes.map((ideia) => (
                    <div 
                      key={ideia.id}
                      className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800 flex items-start justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={
                            ideia.tipo === 'reclamacao' ? 'bg-red-500/20 text-red-400' :
                            ideia.tipo === 'sugestao' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          }>
                            {ideia.tipo === 'reclamacao' ? 'Reclamação' : 
                             ideia.tipo === 'sugestao' ? 'Sugestão' : 'Elogio'}
                          </Badge>
                          <span className="text-xs text-zinc-500">
                            {ideia.created_at && format(parseISO(ideia.created_at), "dd/MM/yyyy")}
                          </span>
                        </div>
                        <p className="text-zinc-300">{ideia.descricao}</p>
                        {ideia.autor_nome && (
                          <p className="text-xs text-zinc-500 mt-2">Por: {ideia.autor_nome}</p>
                        )}
                      </div>
                      {isAdmin() && cicloAtual && (
                        <Button 
                          size="sm"
                          onClick={() => handleImportarIdeia(ideia)}
                          className="bg-cyan-600 hover:bg-cyan-700 ml-4"
                          disabled={saving}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Importar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma reclamação ou sugestão pendente</p>
                  <p className="text-xs mt-1">As ideias do Banco de Ideias aparecerão aqui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Histórico */}
        <TabsContent value="historico" className="mt-4">
          <Card className="bg-[#141416] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Histórico de Ciclos</CardTitle>
            </CardHeader>
            <CardContent>
              {ciclos.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Ciclo</TableHead>
                      <TableHead className="text-zinc-400">Período</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                      <TableHead className="text-zinc-400 text-center">Problemas</TableHead>
                      <TableHead className="text-zinc-400 text-center">Concluídos</TableHead>
                      <TableHead className="text-zinc-400 text-center">Progresso</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ciclos.map((ciclo) => {
                      const res = getResumo(ciclo);
                      const prog = calcularProgresso(ciclo);
                      return (
                        <TableRow key={ciclo.id} className="border-zinc-800">
                          <TableCell className="text-white font-medium">{ciclo.titulo}</TableCell>
                          <TableCell className="text-zinc-400 text-sm">
                            {ciclo.data_inicio && format(parseISO(ciclo.data_inicio), "dd/MM/yyyy")}
                            {ciclo.data_fim && ` - ${format(parseISO(ciclo.data_fim), "dd/MM/yyyy")}`}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${CICLO_STATUS[ciclo.status]?.color} text-white`}>
                              {CICLO_STATUS[ciclo.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center text-zinc-300">{res.total}</TableCell>
                          <TableCell className="text-center text-emerald-400">{res.concluidos}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center gap-2">
                              <Progress value={prog} className="h-2 w-20 bg-zinc-800" />
                              <span className="text-xs text-zinc-400">{prog}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum ciclo registrado ainda</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Novo Ciclo */}
      <Dialog open={showNovoCiclo} onOpenChange={setShowNovoCiclo}>
        <DialogContent className="bg-[#1a1a1e] border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Novo Ciclo de Melhoria</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Crie um novo ciclo para acompanhar problemas e melhorias
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Título do Ciclo *</Label>
              <Input
                value={novoCicloForm.titulo}
                onChange={(e) => setNovoCicloForm({...novoCicloForm, titulo: e.target.value})}
                placeholder="Ex: Junho 2026 - Melhoria de Processos"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Data Início *</Label>
                <Input
                  type="date"
                  value={novoCicloForm.data_inicio}
                  onChange={(e) => setNovoCicloForm({...novoCicloForm, data_inicio: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Data Fim (Previsão)</Label>
                <Input
                  type="date"
                  value={novoCicloForm.data_fim}
                  onChange={(e) => setNovoCicloForm({...novoCicloForm, data_fim: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-300">Descrição/Contexto</Label>
              <Textarea
                value={novoCicloForm.descricao}
                onChange={(e) => setNovoCicloForm({...novoCicloForm, descricao: e.target.value})}
                placeholder="Descreva os objetivos e contexto deste ciclo de melhoria..."
                className="bg-zinc-900 border-zinc-700 text-white min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoCiclo(false)}>Cancelar</Button>
            <Button onClick={handleCriarCiclo} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Criar Ciclo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Novo Problema */}
      <Dialog open={showNovoProblema} onOpenChange={setShowNovoProblema}>
        <DialogContent className="bg-[#1a1a1e] border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar Problema ao Ciclo</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Registre um problema identificado e sua solução proposta
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Descrição do Problema *</Label>
              <Textarea
                value={novoProblemaForm.descricao}
                onChange={(e) => setNovoProblemaForm({...novoProblemaForm, descricao: e.target.value})}
                placeholder="Descreva o problema de forma clara..."
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Causa Raiz</Label>
              <Textarea
                value={novoProblemaForm.causa_raiz}
                onChange={(e) => setNovoProblemaForm({...novoProblemaForm, causa_raiz: e.target.value})}
                placeholder="Qual a causa raiz deste problema?"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>
            
            <div>
              <Label className="text-zinc-300">Solução Proposta</Label>
              <Textarea
                value={novoProblemaForm.solucao_proposta}
                onChange={(e) => setNovoProblemaForm({...novoProblemaForm, solucao_proposta: e.target.value})}
                placeholder="Como pretende resolver este problema?"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-300">Responsável</Label>
                <Input
                  value={novoProblemaForm.responsavel}
                  onChange={(e) => setNovoProblemaForm({...novoProblemaForm, responsavel: e.target.value})}
                  placeholder="Nome"
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Data Prevista</Label>
                <Input
                  type="date"
                  value={novoProblemaForm.data_prevista}
                  onChange={(e) => setNovoProblemaForm({...novoProblemaForm, data_prevista: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Prioridade</Label>
                <Select
                  value={novoProblemaForm.prioridade}
                  onValueChange={(v) => setNovoProblemaForm({...novoProblemaForm, prioridade: v})}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoProblema(false)}>Cancelar</Button>
            <Button onClick={handleAdicionarProblema} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Adicionar Problema
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Problema */}
      <Dialog open={showEditProblema} onOpenChange={setShowEditProblema}>
        <DialogContent className="bg-[#1a1a1e] border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Problema</DialogTitle>
          </DialogHeader>
          
          {problemaEdit && (
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Descrição do Problema *</Label>
                <Textarea
                  value={problemaEdit.descricao}
                  onChange={(e) => setProblemaEdit({...problemaEdit, descricao: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Causa Raiz</Label>
                <Textarea
                  value={problemaEdit.causa_raiz || ''}
                  onChange={(e) => setProblemaEdit({...problemaEdit, causa_raiz: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Solução Proposta</Label>
                <Textarea
                  value={problemaEdit.solucao_proposta || ''}
                  onChange={(e) => setProblemaEdit({...problemaEdit, solucao_proposta: e.target.value})}
                  className="bg-zinc-900 border-zinc-700 text-white"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-300">Responsável</Label>
                  <Input
                    value={problemaEdit.responsavel || ''}
                    onChange={(e) => setProblemaEdit({...problemaEdit, responsavel: e.target.value})}
                    className="bg-zinc-900 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Data Prevista</Label>
                  <Input
                    type="date"
                    value={problemaEdit.data_prevista || ''}
                    onChange={(e) => setProblemaEdit({...problemaEdit, data_prevista: e.target.value})}
                    className="bg-zinc-900 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Status</Label>
                  <Select
                    value={problemaEdit.status}
                    onValueChange={(v) => setProblemaEdit({...problemaEdit, status: v})}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="em_andamento">Em Andamento</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProblema(false)}>Cancelar</Button>
            <Button onClick={handleEditarProblema} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CicloMelhoriaPage;
